/**
 * WorldLogicModule — the in-world authoring surface for World Logic (P5).
 *
 * This is System Beta's behaviour layer made tangible: a non-programmer wires a
 * graph in-world (here, a template + a couple of fields — the MVP node editor),
 * the server compiles it to a SIGNED, deterministic Behavioural Contract, and
 * the owner grants it capabilities before it can touch anything. The panel is a
 * thin presentation layer over the authoritative `compileContract` /
 * `grantContractCaps` / `runContractTick` reducers and simply mirrors the
 * replicated `contract` / `contract_state` / `contract_event` rows:
 *
 *   • Template library — Door / Trivia / Timer / Spawner. Picking a template and
 *     filling its fields builds a `NodeGraph` (the SOURCE OF TRUTH) client-side
 *     and shows a plain-language "wiring" preview that updates as you type.
 *   • Capability request — every template DECLARES the capabilities its graph
 *     exercises; the owner still has to GRANT them, so a fresh `objects.write`
 *     door cannot move anything until it is approved.
 *   • Compile — lowers the graph to a contract via the server compiler. A
 *     `script`-kind asset (P2 provenance) backs the contract; the module reuses
 *     one if the player owns it, else mints one (paying the P3 upload sink once).
 *   • Contract list — each contract's throttle badge (active / throttled /
 *     disabled — over-budget contracts are THROTTLED, never banned), its
 *     requested-vs-granted capability chips with a Grant button (owner/admin),
 *     and a Run button that fires the contract (interact / tick).
 *   • Compiled-Lua preview — the AUTHORITATIVE compiled Lua, read straight from
 *     the replicated `contract.bytecode` (the exact "sandboxed Lua contract" the
 *     graph compiled to; this is also the artifact EngineX's Wasmoon runtime
 *     would hot-reload for a non-authoritative client preview). The server
 *     interpreter is the only authority for capabilities, CPU and memory.
 *   • Event log — the deterministic, replicated `contract_event` trail for the
 *     selected contract (world events, ticket-issue intents, tip intents,
 *     throttle and cross-world-denial notices). All clients see it identically.
 *
 * Boundaries mirrored client-side (the server re-enforces every one): a
 * capability never crosses the owning world; `tickets.issue` (P8) and
 * `economy.tip` (P10) record INTENT only — no ticket settles and no SOV moves
 * through a P5 contract.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import type { Capability, ContractTemplateName, NodeGraph } from '@shared/types/scripting.js';
import { CONTRACT_CAP, CONTRACT_TEMPLATE } from '@shared/constants.js';

// ─── Client-local template builders ─────────────────────────────────────────────
//
// These build the SAME `NodeGraph` shapes the server's compiler exposes
// (`lib/compiler.ts` buildDoorGraph/buildTriviaGraph/buildTimerGraph/
// buildSpawnerGraph). They are intentionally MIRRORED here, not imported from
// the server lib: that module pulls in the bytecode signing salt
// (`signBytecode`), which must never reach a client bundle. Keep these in sync
// with the server builders — the server re-derives the used capabilities from
// the compiled graph and rejects any declared/used capability mismatch, so a
// drift here fails loudly at compile time rather than silently miscompiling.

function buildDoorGraph(objectId: number): NodeGraph {
  return {
    version: 1,
    template: CONTRACT_TEMPLATE.Door,
    nodes: [
      { id: 'ev', kind: 'event.interact' },
      {
        id: 'toggle',
        kind: 'action.toggle',
        params: { key: 'open', objectId, field: 'rotY', onValue: 1.5708, offValue: 0 },
      },
    ],
    edges: [{ from: 'ev', to: 'toggle', kind: 'exec' }],
  };
}

function buildTriviaGraph(correctAnswer: string): NodeGraph {
  return {
    version: 1,
    template: CONTRACT_TEMPLATE.Trivia,
    nodes: [
      { id: 'ev', kind: 'event.interact' },
      { id: 'answer', kind: 'value.input', params: { path: 'answer' } },
      { id: 'correct', kind: 'value.const', params: { value: correctAnswer } },
      { id: 'cmp', kind: 'logic.compare', params: { op: 'eq' } },
      { id: 'branch', kind: 'logic.branch' },
      { id: 'win', kind: 'action.emit', params: { type: 'trivia.correct' } },
      { id: 'reward', kind: 'action.ticket', params: { kind: 'trivia.reward' } },
      { id: 'lose', kind: 'action.emit', params: { type: 'trivia.wrong' } },
    ],
    edges: [
      { from: 'ev', to: 'branch', kind: 'exec' },
      { from: 'answer', to: 'cmp', kind: 'data', toPort: 'a' },
      { from: 'correct', to: 'cmp', kind: 'data', toPort: 'b' },
      { from: 'cmp', to: 'branch', kind: 'data', toPort: 'cond' },
      { from: 'branch', to: 'win', kind: 'exec', fromPort: 'then' },
      { from: 'win', to: 'reward', kind: 'exec' },
      { from: 'branch', to: 'lose', kind: 'exec', fromPort: 'else' },
    ],
  };
}

function buildTimerGraph(everyTicks: number, eventType: string): NodeGraph {
  return {
    version: 1,
    template: CONTRACT_TEMPLATE.Timer,
    nodes: [
      { id: 'ev', kind: 'event.tick', params: { everyTicks } },
      { id: 'fire', kind: 'action.emit', params: { type: eventType } },
    ],
    edges: [{ from: 'ev', to: 'fire', kind: 'exec' }],
  };
}

function buildSpawnerGraph(prefab: string, everyTicks: number): NodeGraph {
  return {
    version: 1,
    template: CONTRACT_TEMPLATE.Spawner,
    nodes: [
      { id: 'ev', kind: 'event.tick', params: { everyTicks } },
      { id: 'spawn', kind: 'action.spawn', params: { prefab } },
    ],
    edges: [{ from: 'ev', to: 'spawn', kind: 'exec' }],
  };
}

/** The capabilities each template DECLARES (must cover what its graph uses). */
const TEMPLATE_CAPS: Record<ContractTemplateName, readonly Capability[]> = {
  door: [CONTRACT_CAP.ObjectsWrite],
  trivia: [CONTRACT_CAP.WorldEventsEmit, CONTRACT_CAP.TicketsIssue],
  timer: [CONTRACT_CAP.WorldEventsEmit],
  spawner: [CONTRACT_CAP.ObjectsWrite],
};

/** Whether a template's contract is driven by `interact` (vs a periodic `tick`). */
function isInteractTemplate(t: ContractTemplateName): boolean {
  return t === CONTRACT_TEMPLATE.Door || t === CONTRACT_TEMPLATE.Trivia;
}

// ─── Small helpers ───────────────────────────────────────────────────────────────

function parseCapList(json: string): Capability[] {
  if (!json) return [];
  try {
    const raw = JSON.parse(json) as unknown;
    return Array.isArray(raw) ? (raw.filter((c) => typeof c === 'string') as Capability[]) : [];
  } catch {
    return [];
  }
}

/** Pull the readable Lua mirror out of the replicated `contract.bytecode`. */
function luaOf(bytecode: string): string {
  try {
    const obj = JSON.parse(bytecode) as { lua?: unknown };
    return typeof obj.lua === 'string' ? obj.lua : '(no preview available)';
  } catch {
    return '(bytecode unreadable)';
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Parse a `world_role.caps` JSON for the two caps we gate on, with role fallback. */
function roleCaps(capsJson: string, role: string): { script: boolean; admin: boolean } {
  const isAdmin = role === 'owner' || role === 'admin';
  const base = { script: isAdmin || role === 'script', admin: isAdmin };
  if (!capsJson) return base;
  try {
    const raw = JSON.parse(capsJson) as Partial<Record<string, unknown>>;
    return {
      script: typeof raw.script === 'boolean' ? raw.script : base.script,
      admin: typeof raw.admin === 'boolean' ? raw.admin : base.admin,
    };
  } catch {
    return base;
  }
}

export class WorldLogicModule implements IModule {
  readonly name = 'WorldLogic';

  // Panel elements.
  private panel!: HTMLElement;
  private worldLabel!: HTMLElement;
  private tplSel!: HTMLSelectElement;
  private paramsEl!: HTMLElement;
  private capsEl!: HTMLElement;
  private summaryEl!: HTMLElement;
  private compileBtn!: HTMLButtonElement;
  private listEl!: HTMLElement;
  private luaEl!: HTMLElement;
  private eventsEl!: HTMLElement;
  private noteEl!: HTMLElement;

  private open = false;
  private dirty = true;
  private bound = false;

  /** Selected contract for the Lua + event panes. */
  private selectedContractId: bigint | null = null;
  /** Monotonic tick number fed to tick-driven contracts on each Run. */
  private tickCounter = 0;
  /** Graph + caps awaiting a freshly-minted script asset (auto-chained compile).
   *  worldId is snapshotted at stash time — a world-hop between stash and asset
   *  arrival must abort rather than compile into the wrong world. */
  private pendingCompile: { graphJson: string; caps: string; worldId: bigint } | null = null;

  init(app: App): void {
    this.panel = document.getElementById('worldlogic')!;
    this.worldLabel = document.getElementById('wl-world')!;
    this.tplSel = document.getElementById('wl-template') as HTMLSelectElement;
    this.paramsEl = document.getElementById('wl-params')!;
    this.capsEl = document.getElementById('wl-caps')!;
    this.summaryEl = document.getElementById('wl-summary')!;
    this.compileBtn = document.getElementById('wl-compile') as HTMLButtonElement;
    this.listEl = document.getElementById('wl-list')!;
    this.luaEl = document.getElementById('wl-lua')!;
    this.eventsEl = document.getElementById('wl-events')!;
    this.noteEl = document.getElementById('wl-note')!;

    this.tplSel.addEventListener('change', () => this.renderEditor());
    this.compileBtn.addEventListener('click', () => this.compile());
    (document.getElementById('wl-close') as HTMLButtonElement).addEventListener('click', () => this.close());

    // L toggles; Escape closes. Ignore while typing into a field.
    window.addEventListener('keydown', (e) => {
      const typing =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement;
      if (e.key === 'Escape' && this.open) {
        this.close();
      } else if ((e.key === 'l' || e.key === 'L') && !typing && atlas.hasAccount) {
        e.preventDefault();
        this.toggle();
      }
    });

    // Switching worlds changes which contracts are visible + the door targets.
    // Also drop any pending compile — it was targeting the previous world and
    // must not be forwarded to the new one when the script asset arrives.
    bus.on('atlas:world-entered', () => {
      this.selectedContractId = null;
      this.pendingCompile = null;
      this.dirty = true;
      if (this.open) this.renderEditor();
    });
    bus.on('atlas:world-cleared', () => {
      this.dirty = true;
      if (this.open) this.renderEditor();
    });
    // Door target dropdown tracks placed objects.
    const objChanged = (): void => {
      if (this.open && this.tplSel.value === CONTRACT_TEMPLATE.Door) this.renderEditor();
    };
    bus.on('atlas:object-added', objChanged);
    bus.on('atlas:object-removed', objChanged);

    app.world.addSystem(makeSystem('WorldLogic', Phase.UI, 26, () => this.tick()));
  }

  // ─── Lazy table binding ───────────────────────────────────────────────────────

  /** The connection appears asynchronously; bind contract-table callbacks once. */
  private ensureBound(): void {
    if (this.bound || !atlas.conn) return;
    const conn = atlas.conn;
    this.bound = true;

    const touch = (): void => {
      this.dirty = true;
    };
    conn.db.contract.onInsert(touch);
    conn.db.contract.onUpdate(touch);
    conn.db.contract.onDelete(touch);
    conn.db.contractState.onInsert(touch);
    conn.db.contractState.onUpdate(touch);
    conn.db.contractEvent.onInsert(touch);

    // A freshly-minted script asset may be what an in-flight compile is waiting
    // for — auto-chain into the compile once it arrives.
    conn.db.asset.onInsert((_ctx, row) => this.onAsset(row.assetId, row.kind, row.creator.toHexString()));
    this.dirty = true;
  }

  // ─── Per-frame tick ────────────────────────────────────────────────────────────

  private tick(): void {
    this.ensureBound();
    if (!this.open) return;
    if (this.dirty) {
      this.dirty = false;
      this.renderContracts();
    }
  }

  // ─── Open / close ────────────────────────────────────────────────────────────

  private toggle(): void {
    if (this.open) this.close();
    else this.openPanel();
  }

  private openPanel(): void {
    this.open = true;
    this.dirty = true;
    this.panel.classList.add('show');
    this.renderEditor();
    this.renderContracts();
  }

  private close(): void {
    this.open = false;
    this.panel.classList.remove('show');
  }

  // ─── Capability gating (mirror of the server; server is authoritative) ──────────

  private currentTemplate(): ContractTemplateName {
    return this.tplSel.value as ContractTemplateName;
  }

  /** The local player's (script, admin) standing in a world (owner ⇒ both). */
  private localStanding(worldId: bigint): { script: boolean; admin: boolean } {
    const conn = atlas.conn;
    const me = atlas.identityHex;
    if (!conn || !me) return { script: false, admin: false };
    const w = conn.db.world.worldId.find(worldId);
    if (w && w.owner.toHexString() === me) return { script: true, admin: true };
    for (const r of conn.db.worldRole.iter()) {
      if (r.worldId === worldId && r.identity.toHexString() === me) return roleCaps(r.caps, r.role);
    }
    return { script: false, admin: false };
  }

  /** First `script`-kind asset the local player owns, for reuse as a contract backer. */
  private myScriptAssetId(): bigint | null {
    const me = atlas.identityHex;
    if (!me) return null;
    for (const a of atlas.assets.values()) {
      if (a.kind === 'script' && a.creator === me) return a.assetId;
    }
    return null;
  }

  // ─── Editor (template + params + caps + live wiring summary) ─────────────────────

  private renderEditor(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    const tpl = this.currentTemplate();

    this.worldLabel.textContent =
      conn && worldId !== null ? (conn.db.world.worldId.find(worldId)?.name ?? `#${worldId}`) : '';

    // Param fields per template.
    this.paramsEl.innerHTML = this.paramsHtml(tpl);
    // Re-wire param inputs to refresh the wiring summary live.
    for (const el of this.paramsEl.querySelectorAll('input, select')) {
      el.addEventListener('input', () => this.renderSummary());
    }

    // Capability request chips.
    this.capsEl.innerHTML = TEMPLATE_CAPS[tpl]
      .map((c) => `<span class="wl-cap req">${c}</span>`)
      .join('');

    this.renderSummary();

    // Compile gate mirrors the server `script` capability.
    const can = worldId !== null && this.localStanding(worldId).script;
    this.compileBtn.disabled = !can;
    this.compileBtn.title = can ? '' : 'Needs the "script" capability in this world';
  }

  private paramsHtml(tpl: ContractTemplateName): string {
    switch (tpl) {
      case CONTRACT_TEMPLATE.Door: {
        const opts = [...atlas.worldObjects.values()]
          .map(
            (o) =>
              `<option value="${o.objectId.toString()}">#${o.objectId.toString()} · ${escapeHtml(o.prefabId)}</option>`,
          )
          .join('');
        return (
          `<label>Target object` +
          `<select id="wl-door-target">${opts || '<option value="">(no objects in this world)</option>'}</select>` +
          `</label>` +
          `<div class="wl-hint">A player interacting with the bound object toggles it open/closed (rotY).</div>`
        );
      }
      case CONTRACT_TEMPLATE.Trivia:
        return (
          `<label>Correct answer` +
          `<input id="wl-trivia-answer" type="text" maxlength="64" placeholder="42" value="42" /></label>` +
          `<div class="wl-hint">A correct interact answer emits an event + a ticket-issue intent; a wrong one emits a miss.</div>`
        );
      case CONTRACT_TEMPLATE.Timer:
        return (
          `<label>Every N ticks` +
          `<input id="wl-timer-every" type="number" min="1" max="3600" value="60" /></label>` +
          `<label>Event type` +
          `<input id="wl-timer-type" type="text" maxlength="48" placeholder="timer.tick" value="timer.tick" /></label>` +
          `<div class="wl-hint">Fires a world event every N server ticks.</div>`
        );
      case CONTRACT_TEMPLATE.Spawner:
        return (
          `<label>Prefab` +
          `<input id="wl-spawner-prefab" type="text" maxlength="48" placeholder="cube" value="cube" /></label>` +
          `<label>Every N ticks` +
          `<input id="wl-spawner-every" type="number" min="1" max="3600" value="120" /></label>` +
          `<div class="wl-hint">Places a prefab every N ticks (the server enforces a spawn cap).</div>`
        );
      default:
        return '';
    }
  }

  /** Build the `NodeGraph` for the current editor state (source of truth). */
  private buildGraph(): NodeGraph | null {
    const tpl = this.currentTemplate();
    switch (tpl) {
      case CONTRACT_TEMPLATE.Door: {
        const sel = document.getElementById('wl-door-target') as HTMLSelectElement | null;
        const v = sel?.value ?? '';
        if (!v) return null;
        return buildDoorGraph(Number(v));
      }
      case CONTRACT_TEMPLATE.Trivia: {
        const inp = document.getElementById('wl-trivia-answer') as HTMLInputElement | null;
        return buildTriviaGraph((inp?.value ?? '').trim() || '42');
      }
      case CONTRACT_TEMPLATE.Timer: {
        const every = document.getElementById('wl-timer-every') as HTMLInputElement | null;
        const type = document.getElementById('wl-timer-type') as HTMLInputElement | null;
        const n = Math.max(1, Math.floor(Number(every?.value) || 60));
        return buildTimerGraph(n, (type?.value ?? '').trim() || 'timer.tick');
      }
      case CONTRACT_TEMPLATE.Spawner: {
        const prefab = document.getElementById('wl-spawner-prefab') as HTMLInputElement | null;
        const every = document.getElementById('wl-spawner-every') as HTMLInputElement | null;
        const n = Math.max(1, Math.floor(Number(every?.value) || 120));
        return buildSpawnerGraph((prefab?.value ?? '').trim() || 'cube', n);
      }
      default:
        return null;
    }
  }

  /** A plain-language description of the wired graph (the live "node" preview). */
  private renderSummary(): void {
    const tpl = this.currentTemplate();
    const lines: string[] = [];
    switch (tpl) {
      case CONTRACT_TEMPLATE.Door: {
        const sel = document.getElementById('wl-door-target') as HTMLSelectElement | null;
        const id = sel?.value || '—';
        lines.push('On a player <b>interact</b>:');
        lines.push(`&nbsp;&nbsp;→ toggle object <b>#${escapeHtml(id)}</b> field <b>rotY</b> (open ⇄ closed)`);
        break;
      }
      case CONTRACT_TEMPLATE.Trivia: {
        const inp = document.getElementById('wl-trivia-answer') as HTMLInputElement | null;
        const ans = (inp?.value ?? '').trim() || '42';
        lines.push('On a player <b>interact</b> (with their <code>answer</code>):');
        lines.push(`&nbsp;&nbsp;if answer == <b>"${escapeHtml(ans)}"</b> → emit <b>trivia.correct</b> + issue a ticket`);
        lines.push('&nbsp;&nbsp;else → emit <b>trivia.wrong</b>');
        break;
      }
      case CONTRACT_TEMPLATE.Timer: {
        const every = document.getElementById('wl-timer-every') as HTMLInputElement | null;
        const type = document.getElementById('wl-timer-type') as HTMLInputElement | null;
        const n = Math.max(1, Math.floor(Number(every?.value) || 60));
        const t = (type?.value ?? '').trim() || 'timer.tick';
        lines.push(`Every <b>${n}</b> server tick(s):`);
        lines.push(`&nbsp;&nbsp;→ emit world event <b>${escapeHtml(t)}</b>`);
        break;
      }
      case CONTRACT_TEMPLATE.Spawner: {
        const prefab = document.getElementById('wl-spawner-prefab') as HTMLInputElement | null;
        const every = document.getElementById('wl-spawner-every') as HTMLInputElement | null;
        const n = Math.max(1, Math.floor(Number(every?.value) || 120));
        const p = (prefab?.value ?? '').trim() || 'cube';
        lines.push(`Every <b>${n}</b> server tick(s):`);
        lines.push(`&nbsp;&nbsp;→ spawn prefab <b>${escapeHtml(p)}</b> (capped)`);
        break;
      }
    }
    this.summaryEl.innerHTML = lines.join('<br>');
  }

  // ─── Compile (mint a script asset if needed, then compileContract) ───────────────

  private setNote(text: string, cls: '' | 'ok' | 'err' = ''): void {
    this.noteEl.textContent = text;
    this.noteEl.className = cls;
  }

  private compile(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null) return;

    const graph = this.buildGraph();
    if (!graph) {
      this.setNote('Fill in the template fields first (e.g. pick a target object).', 'err');
      return;
    }
    const graphJson = JSON.stringify(graph);
    const caps = JSON.stringify(TEMPLATE_CAPS[this.currentTemplate()]);

    const assetId = this.myScriptAssetId();
    if (assetId !== null) {
      this.doCompile(assetId, graphJson, caps, worldId);
      return;
    }

    // No script asset yet — mint one (pays the upload sink once) and auto-chain.
    // Snapshot worldId so a world-hop before the asset arrives doesn't compile
    // the contract into the wrong world.
    this.pendingCompile = { graphJson, caps, worldId };
    this.setNote('Creating a script asset…');
    void conn.reducers
      .createAsset({
        kind: 'script',
        contentRef: 'atlas://worldlogic/contract',
        permCopy: false,
        permModify: true,
        permTransfer: false,
        permRemix: false,
        permTemplate: false,
        isPremium: false,
        polyCount: 0n,
        textureBytes: 0n,
      })
      .catch((err: Error) => {
        this.pendingCompile = null;
        this.setNote(`could not create script asset: ${err.message}`, 'err');
      });
  }

  /** A new asset row arrived — if a compile is waiting on a script asset, run it. */
  private onAsset(assetId: bigint, kind: string, creatorHex: string): void {
    if (!this.pendingCompile || kind !== 'script' || creatorHex !== atlas.identityHex) return;
    const { graphJson, caps, worldId } = this.pendingCompile;
    this.pendingCompile = null;
    // Abort if the player hopped to a different world since the compile was staged —
    // compiling into the wrong world would silently bind the contract to the previous one.
    if (atlas.currentWorldId !== worldId) {
      this.setNote('World changed before the asset arrived — re-compile in the new world.', 'err');
      return;
    }
    this.doCompile(assetId, graphJson, caps, worldId);
  }

  private doCompile(assetId: bigint, graphJson: string, capsRequested: string, worldId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.setNote('Compiling contract…');
    void conn.reducers
      .compileContract({ worldId, assetId, graphJson, capsRequested })
      .then(() => this.setNote('Compiled — grant its capabilities to activate it.', 'ok'))
      .catch((err: Error) => this.setNote(`compile failed: ${err.message}`, 'err'));
  }

  // ─── Contract list + Lua preview + event log ─────────────────────────────────────

  private renderContracts(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null) {
      this.listEl.innerHTML = '<div class="wl-empty">Enter a world to author logic.</div>';
      this.luaEl.textContent = '';
      this.eventsEl.innerHTML = '';
      return;
    }

    const canAdmin = this.localStanding(worldId).admin;
    const rows = [...conn.db.contract.iter()]
      .filter((c) => c.worldId === worldId)
      .sort((a, b) => (a.contractId < b.contractId ? -1 : a.contractId > b.contractId ? 1 : 0));

    if (rows.length === 0) {
      this.listEl.innerHTML = '<div class="wl-empty">No contracts in this world yet.</div>';
    } else {
      // Keep a valid selection.
      if (this.selectedContractId === null || !rows.some((r) => r.contractId === this.selectedContractId)) {
        this.selectedContractId = rows[0]!.contractId;
      }
      this.listEl.innerHTML = '';
      for (const c of rows) {
        const requested = parseCapList(c.capsRequested);
        const granted = new Set(parseCapList(c.capsGranted).map((x) => x as string));
        const tpl = (c.graphJson && this.templateOf(c.graphJson)) || 'door';
        const selected = c.contractId === this.selectedContractId;

        const el = document.createElement('div');
        el.className = `wl-row ${selected ? 'sel' : ''}`;

        const caps =
          requested
            .map((cap) => `<span class="wl-cap ${granted.has(cap) ? 'granted' : 'pending'}">${cap}</span>`)
            .join('') || '<span class="wl-cap none">no caps</span>';

        const fullyGranted = requested.length > 0 && requested.every((cap) => granted.has(cap));
        const grantBtn =
          canAdmin && !fullyGranted
            ? `<button class="wl-grant" data-grant="${c.contractId.toString()}">Grant</button>`
            : '';
        const runLabel = isInteractTemplate(tpl) ? 'Run interact' : 'Run tick';
        const runDisabled = !fullyGranted ? 'disabled title="grant its capabilities first"' : '';
        const answerInput =
          tpl === CONTRACT_TEMPLATE.Trivia && fullyGranted
            ? `<input class="wl-answer" data-answer="${c.contractId.toString()}" type="text" placeholder="answer" value="42" />`
            : '';

        el.innerHTML =
          `<div class="wl-row-head">` +
          `<span class="wl-id">#${c.contractId.toString()} · ${tpl}</span>` +
          `<span class="wl-badge ${c.state}">${c.state}</span>` +
          `</div>` +
          `<div class="wl-row-caps">${caps}</div>` +
          `<div class="wl-row-ctl">` +
          grantBtn +
          answerInput +
          `<button class="wl-run" data-run="${c.contractId.toString()}" ${runDisabled}>${runLabel}</button>` +
          `<button class="wl-view" data-view="${c.contractId.toString()}">View</button>` +
          `</div>`;

        const grant = el.querySelector<HTMLButtonElement>('[data-grant]');
        if (grant)
          grant.addEventListener('click', () => this.grant(BigInt(grant.dataset.grant!), c.capsRequested));
        const run = el.querySelector<HTMLButtonElement>('[data-run]');
        if (run) run.addEventListener('click', () => this.run(BigInt(run.dataset.run!), tpl, el));
        const view = el.querySelector<HTMLButtonElement>('[data-view]');
        if (view)
          view.addEventListener('click', () => {
            this.selectedContractId = BigInt(view.dataset.view!);
            this.dirty = true;
          });

        this.listEl.appendChild(el);
      }
    }

    this.renderDetail();
  }

  /** The compiled-Lua preview + event log for the selected contract. */
  private renderDetail(): void {
    const conn = atlas.conn;
    const id = this.selectedContractId;
    if (!conn || id === null) {
      this.luaEl.textContent = '';
      this.eventsEl.innerHTML = '';
      return;
    }
    const c = conn.db.contract.contractId.find(id);
    this.luaEl.textContent = c ? luaOf(c.bytecode) : '';

    const events = [...conn.db.contractEvent.iter()]
      .filter((e) => e.contractId === id)
      .sort((a, b) => (a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0))
      .slice(-30);
    if (events.length === 0) {
      this.eventsEl.innerHTML = '<div class="wl-empty">No events yet — Run the contract.</div>';
      return;
    }
    this.eventsEl.innerHTML = events
      .map((e) => {
        const cls = e.type === 'throttled' || e.type === 'denied' ? 'warn' : '';
        return (
          `<div class="wl-ev ${cls}">` +
          `<span class="wl-ev-type">${escapeHtml(e.type)}</span>` +
          `<span class="wl-ev-payload">${escapeHtml(e.payload)}</span>` +
          `</div>`
        );
      })
      .join('');
  }

  private templateOf(graphJson: string): ContractTemplateName | null {
    try {
      const g = JSON.parse(graphJson) as { template?: unknown };
      const t = g.template;
      if (
        t === CONTRACT_TEMPLATE.Door ||
        t === CONTRACT_TEMPLATE.Trivia ||
        t === CONTRACT_TEMPLATE.Timer ||
        t === CONTRACT_TEMPLATE.Spawner
      ) {
        return t;
      }
      return null;
    } catch {
      return null;
    }
  }

  // ─── Reducer actions on a contract ───────────────────────────────────────────────

  private grant(contractId: bigint, capsRequested: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    // Grant exactly what the contract requested; the server intersects with the
    // requested set, so this never widens it beyond its declaration.
    this.setNote('Granting capabilities…');
    void conn.reducers
      .grantContractCaps({ contractId, capsGranted: capsRequested })
      .then(() => this.setNote(`Granted capabilities to contract #${contractId.toString()}.`, 'ok'))
      .catch((err: Error) => this.setNote(`grant failed: ${err.message}`, 'err'));
  }

  private run(contractId: bigint, tpl: ContractTemplateName, rowEl: HTMLElement): void {
    const conn = atlas.conn;
    if (!conn) return;
    let inputs: Record<string, string | number>;
    if (tpl === CONTRACT_TEMPLATE.Trivia) {
      const inp = rowEl.querySelector<HTMLInputElement>('[data-answer]');
      inputs = { event: 'interact', answer: (inp?.value ?? '').trim() };
    } else if (isInteractTemplate(tpl)) {
      inputs = { event: 'interact' };
    } else {
      inputs = { event: 'tick', tick: this.tickCounter++ };
    }
    this.setNote('Running contract…');
    void conn.reducers
      .runContractTick({ contractId, inputs: JSON.stringify(inputs) })
      .then(() => this.setNote(`Ran contract #${contractId.toString()} — see its events.`, 'ok'))
      .catch((err: Error) => this.setNote(`run failed: ${err.message}`, 'err'));
  }

  dispose(): void {
    // No retained resources beyond DOM listeners (cleaned up with the document).
  }
}
